<template>
  <div role="form">
    <div class="border-bottom">
      <h1 class="form-section-title">Create a merge job</h1>
      <p>
        Use merge jobs to join duplicate records together. <a href="https://help.wrestlingiq.com/article/90-merging-accounts" target="_blank" class="text-underline">Read more here.</a>
      </p>
    </div>
    <div v-if="canonicalExists" class="m-t-md">
      <h2>1. Choose the record to keep</h2>
      <div class="m-t-m m-l-m border rounded-sm p-m bg-gray-50">
        <div class="flex justify-space-between align-items-start">
          <div>
            <p class="text-heavy m-b-none text-16">
              {{ canonical.name }}
              <a target="_blank" rel="noopener noreferrer" :href="'/' + canonical.href" class="m-l-xs">
                <i class="fa-solid fa-up-right-from-square text-14"></i>
              </a>
            </p>
            <p class="text-gray-600 m-b-none text-14">{{ profileDetails(canonical) }}</p>
            <p class="search-type-label m-b-none">
              {{ canonical.readable_type }}
              <span class="text-gray-400 m-l-xs">#{{ canonical.id }}</span>
            </p>
          </div>
          <button type="button" class="btn btn-default btn-xs" @click="canonicalClicked({})">
            Change
          </button>
        </div>
      </div>
    </div>
    <div v-else class="m-t-md">
      <h2 class="text-heavy">1. Choose the record to keep</h2>
      <p class="text-gray-600"><span class="text-heavy">Tip:</span> The record you select here keeps its login. Choose the most active account.</p>
      <search id="search-form" class="m-t-sm"
              :mutate-history="false"
              :display-all-on-empty="false"
              :query-deleted="true"
              :profiles-only="true"
              :with-billing="true"
              query-key="query"
              response-key="results" placeholder="Search by name of parent or wrestler..."
              :base-url="this.$apiService.searchUrl()">
        <template v-slot:list="slotProps">
          <div class="search-result bg-gray-50-hover"
               :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
            <div class='hr-line-dashed'></div>
            <div class="flex justify-space-between align-items-center">
              <div>
                <div class="flex align-items-center">
                  <h3 :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
                    {{ slotProps.result.name }}
                  </h3>
                  <div class="m-l-sm">
                    <a target="_blank" rel="noopener noreferrer" :href="'/' + slotProps.result.href">
                      <i class="fa-solid fa-up-right-from-square text-12"></i>
                    </a>
                  </div>
                </div>
                <p class="text-gray-600 m-b-none text-14" v-if="!isCoach(slotProps.result)">{{ profileDetails(slotProps.result) }}</p>
                <p class='search-type-label' :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
                  {{ slotProps.result.readable_type }}
                  <span class="text-gray-400 m-l-xs">#{{ slotProps.result.id }}</span>
                  <span v-if="isCoach(slotProps.result)">
                    <br>Coaches cannot be merged, but you can delete them. Contact support@wrestlingiq.com if you need assistance.
                  </span>
                </p>
              </div>
              <div v-if="!isCoach(slotProps.result)">
                <button class="btn btn-default bg-white-hover" @click="canonicalClicked(slotProps.result)">Select
                </button>
              </div>
            </div>
          </div>
        </template>
      </search>
    </div>
    <div v-if="bothSelected" class="m-t-md">
      <h2>2. Choose the duplicate record</h2>
      <div class="m-t-m m-l-m border rounded-sm p-m bg-gray-50">
        <div class="flex justify-space-between align-items-start">
          <div>
            <p class="text-heavy m-b-none text-16">
              {{ duplicate.name }}
              <a target="_blank" rel="noopener noreferrer" :href="'/' + duplicate.href" class="m-l-xs">
                <i class="fa-solid fa-up-right-from-square text-14"></i>
              </a>
            </p>
            <p class="text-gray-600 m-b-none text-14">{{ profileDetails(duplicate) }}</p>
            <p class="search-type-label m-b-none">
              {{ duplicate.readable_type }}
              <span class="text-gray-400 m-l-xs">#{{ duplicate.id }}</span>
            </p>
          </div>
          <button type="button" class="btn btn-default btn-xs" @click="duplicateClicked({})">
            Change
          </button>
        </div>
      </div>
    </div>
    <div v-else-if="canonicalExists && !duplicateExists" class="m-t-md">
      <h2 class="text-heavy">2. Choose the duplicate record</h2>
      <search id="search-form-duplicate" class="m-t-sm"
              :mutate-history="false"
              :display-all-on-empty="false"
              :query-deleted="true"
              :profiles-only="true"
              :with-billing="true"
              query-key="query"
              response-key="results" placeholder="Search by name of parent or wrestler..."
              :base-url="this.$apiService.searchUrl()">
        <template v-slot:list="slotProps">
          <div class="search-result bg-gray-50-hover"
               :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
            <div class='hr-line-dashed'></div>
            <div class="flex justify-space-between align-items-center">
              <div>
                <div class="flex align-items-center">
                  <h3 :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
                    {{ slotProps.result.name }}
                  </h3>
                  <div class="m-l-sm">
                    <a target="_blank" rel="noopener noreferrer" :href="'/' + slotProps.result.href">
                      <i class="fa-solid fa-up-right-from-square text-12"></i>
                    </a>
                  </div>
                </div>
                <p class="text-gray-600 m-b-none text-14" v-if="!isCoach(slotProps.result)">{{ profileDetails(slotProps.result) }}</p>
                <p class='search-type-label' :class="isCoach(slotProps.result) ? 'text-gray-400-important' : ''">
                  {{ slotProps.result.readable_type }}
                  <span class="text-gray-400 m-l-xs">#{{ slotProps.result.id }}</span>
                  <span v-if="isCoach(slotProps.result)">
                    <br>Coaches cannot be merged, but you can delete them. Contact support@wrestlingiq.com if you need assistance.
                  </span>
                </p>
              </div>
              <div v-if="!isCoach(slotProps.result)">
                <button class="btn btn-default bg-white-hover" @click="duplicateClicked(slotProps.result)">Select
                </button>
              </div>
            </div>
          </div>
        </template>
      </search>
    </div>
    <div v-if="bothSelected" class="m-t-lg">
      <h2 class="text-heavy">3. Review merge</h2>
      <div v-if="canMerge">
        <div class="border rounded-sm p-m m-t-m bg-gray-50">
          <div class="flex gap-md align-items-start">
            <div class="flex-1">
              <p class="text-12 text-gray-500 m-b-none font-heavy">KEEPING</p>
              <p class="text-heavy m-b-none">
                {{ canonical.name }}
                <span class="text-gray-400 font-normal m-l-xs">#{{ canonical.id }}</span>
                <a target="_blank" rel="noopener noreferrer" :href="'/' + canonical.href" class="m-l-xs"><i class="fa-solid fa-up-right-from-square text-12"></i></a>
              </p>
              <p class="text-gray-600 text-14 m-b-none">{{ profileDetails(canonical) }}</p>
            </div>
            <div class="text-gray-400 p-t-m">
              <i class="fa-solid fa-left-long text-20"></i>
            </div>
            <div class="flex-1">
              <p class="text-12 text-gray-500 m-b-none font-heavy">REMOVING</p>
              <p class="text-heavy m-b-none">
                {{ duplicate.name }}
                <span class="text-gray-400 font-normal m-l-xs">#{{ duplicate.id }}</span>
                <a target="_blank" rel="noopener noreferrer" :href="'/' + duplicate.href" class="m-l-xs"><i class="fa-solid fa-up-right-from-square text-12"></i></a>
              </p>
              <p class="text-gray-600 text-14 m-b-none">{{ profileDetails(duplicate) }}</p>
            </div>
          </div>
        </div>
        <div class="m-t-m">
          <p class="m-b-xs"><strong>What will happen:</strong></p>
          <ul class="m-b-m">
            <li v-if="duplicate.email"><strong>{{ duplicate.name }}</strong> will lose login access</li>
            <li>All data ({{ wrestlerMerge ? 'guardian links, registrations, stats, matches' : 'wrestler links, registrations, charges, subscriptions' }}) transfers to <strong>{{ canonical.name }}</strong></li>
            <li><strong>{{ duplicate.name }}</strong>'s profile will be permanently removed</li>
            <li>Historical reporting will combine both records</li>
          </ul>
        </div>
        <ladda-button @lbClicked="createMergeJob" el-class="btn-primary m-t-m" :loading="loading">
          Create merge job
        </ladda-button>
        <p v-if="error" class="text-danger m-t-sm">
          <i class="fa-solid fa-circle-exclamation"></i> {{ errorMessage }}
        </p>
      </div>
      <div v-else>
        <p v-if="!recordsAreSameType" class="text-danger">
          <i class="fa-solid fa-circle-exclamation"></i> Record types must match (i.e. you cannot merge a parent into a
          wrestler).
          Please change your selection so that both records are either parents or wrestlers.
        </p>
        <p v-else-if="recordsAreSameIdAndType" class="text-danger">
          <i class="fa-solid fa-circle-exclamation"></i> The selected records are already the same.
        </p>
        <p v-else-if="fullIntoGuest" class="text-danger">
          <i class="fa-solid fa-circle-exclamation"></i> Merging full accounts into guest accounts is not supported. If you
          want to merge the guest account into the full one, select the full account in step 1.
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import Search from '../../shared/search.vue';
import GenericSearchResult from './generic-search-result.vue';
import LaddaButton from '../../shared/ladda_button.vue';

export default {
  name: 'merge-job-form',
  components: {
    GenericSearchResult,
    LaddaButton,
    Search,
  },
  props: {
    username: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      canonical: {},
      duplicate: {},
      loading: false,
      error: false,
      errorMessage: '',
    };
  },
  computed: {
    canonicalExists() {
      return Object.keys(this.canonical).length > 0;
    },
    duplicateExists() {
      return Object.keys(this.duplicate).length > 0;
    },
    bothSelected() {
      return this.canonicalExists && this.duplicateExists;
    },
    recordsAreSameType() {
      if (!this.canonicalExists && !this.duplicateExists) {
        return false;
      }

      return this.canonical.type === this.duplicate.type;
    },
    recordsAreSameIdAndType() {
      return this.recordsAreSameType && this.canonical.id === this.duplicate.id;
    },
    fullIntoGuest() {
      return _.startsWith(this.canonical.readable_type, 'Guest') && !_.startsWith(this.duplicate.readable_type, 'Guest');
    },
    canMerge() {
      return (this.parentMerge || this.wrestlerMerge) && !this.recordsAreSameIdAndType && !this.fullIntoGuest;
    },
    parentMerge() {
      return this.recordsAreSameType && this.canonical.type === 'ParentProfile';
    },
    wrestlerMerge() {
      return this.recordsAreSameType && this.canonical.type === 'WrestlerProfile';
    },
  },
  methods: {
    canonicalClicked(profile) {
      const changed = !profile.id
        || this.canonical.id !== profile.id
        || this.canonical.type !== profile.type;
      this.canonical = profile;
      if (changed) {
        this.duplicate = {};
      }
    },
    duplicateClicked(profile) {
      this.duplicate = profile;
    },
    isCoach(result) {
      return result.type === 'CoachProfile';
    },
    profileDetails(profile) {
      const parts = [];
      if (profile.email) {
        parts.push(profile.email);
      } else {
        parts.push('No email');
      }
      if (profile.registrations_count != null) {
        const count = profile.registrations_count;
        parts.push(count + (count === 1 ? ' registration' : ' registrations'));
      }
      if (profile.active_subscriptions_count != null) {
        const count = profile.active_subscriptions_count;
        parts.push(count + (count === 1 ? ' active subscription' : ' active subscriptions'));
      }
      if (profile.created_at) {
        parts.push(`Added ${moment(profile.created_at).format('MMM YYYY')}`);
      }
      return parts.join(' · ');
    },
    mergeMessage() {
      return `Merge created by ${this.username}. Merge duplicate ${this.duplicate.readable_type} (id: ${this.duplicate.id}, name: ${this.duplicate.name}) into ${this.canonical.readable_type} (id: ${this.canonical.id}, name: ${this.canonical.name})`;
    },
    createMergeJob() {
      if (!this.canMerge || this.loading) {
        return;
      }

      const params = {
        merge_job: {
          name: this.mergeMessage(),
          version: 'v1',
          type: this.parentMerge ? 'ParentMergeJob' : 'WrestlerMergeJob',
          canonical_id: this.canonical.id,
          canonical_type: this.canonical.type,
          duplicate_id: this.duplicate.id,
          duplicate_type: this.duplicate.type,
        },
      };

      this.loading = true;
      this.error = false;
      this.errorMessage = '';
      axios.post(this.$apiService.mergeJobsUrl(), params)
        .then((response) => {
          this.$notificationManager.emit('show-toast', `Merge job ${response.data.id} queued`, true);
          window.location.assign('/merge_jobs');
        })
        .catch((error) => {
          this.loading = false;
          this.error = true;
          this.errorMessage = `Error creating merge job: ${error.toString()}`;
        });
    },
  },
};
</script>
